<template>
  <div class="date-rule">
    <b-button variant="link" @click="modals.open = true">{{ selectedTime }}</b-button>
    <DateTimeSelectionModal
      v-model:is-open="modals.open"
      @confirm="onConfirm"
      @cancel-selection="onCancel"
      :is-relative="vm.isRelative"
      :can-be-none="props.canBeNone"
      :time="vm.time"
      :option-key="vm.key"
      :option-value="vm.value" />
  </div>
</template>

<script setup lang="ts">
import DateTimeSelectionModal from "./modals/DateTimeSelectionModal.vue";
import { computed, onMounted, reactive, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment";
import { relativeTimeOptions } from "@util/relativeDate";

const props = defineProps<{ value: string; canBeNone: boolean }>();
const emits = defineEmits(["input", "deleteRule"]);
const { t: $t, d: $d } = useI18n();

const modals = reactive({ open: false });

const localModel = computed({
  get: () => {
    return props.value;
  },
  set: (nv) => {
    emits("input", nv);
  }
});

type ViewModel = {
  isRelative: boolean | null;
  time: string | undefined;
  key: string | undefined;
  value: number | undefined;
  hasValue: boolean;
};

const vm = reactive<ViewModel>({
  isRelative: null,
  time: undefined,
  key: undefined,
  value: undefined,
  hasValue: false
});

watch(
  () => localModel.value,
  (nv) => {
    if (nv === "unselected") {
      vm.isRelative = false;
      return;
    }

    if (nv === "") {
      vm.isRelative = null;
      return;
    }

    const date = moment(nv);
    if (date.isValid()) {
      vm.isRelative = false;
      vm.time = nv;
      return;
    }

    vm.isRelative = true;
    const match = nv.match(/(\d+)/);
    const num = match ? parseInt(match[1], 10) : NaN;

    if (!Number.isNaN(num)) {
      vm.hasValue = true;
      const backendKey = nv.replace(num.toString(), "{value}");
      const key = relativeTimeOptions.find((o) => o.backendKey?.plural === backendKey || o.backendKey?.singular === nv);
      vm.key = key?.key;
      vm.value = num;
    } else {
      vm.hasValue = false;
      const key = relativeTimeOptions.find((o) => o.backendKey?.singular === nv);
      vm.key = key?.key;
    }
  },
  { immediate: true }
);

const selectedTime = computed(() => {
  if (localModel.value === "unselected") return "";

  if (vm.isRelative === null) {
    return `<${$t("empty")}>`;
  }

  if (!vm.isRelative && vm.time) {
    const format = vm.time?.split(" ").length > 1 ? "shortDateTime" : "shortDate";
    return $d(moment(vm.time).toDate(), format);
  }

  if (vm.value && vm.hasValue) {
    return $t(`relativeDateTimePreview.${vm.key}`, { count: vm.value });
  } else {
    return $t(`relativeDateTimePicker.${vm.key}`);
  }
});

const onConfirm = async (data: string) => {
  localModel.value = data;
};

const onCancel = () => {
  if (localModel.value === "unselected") emits("deleteRule");
};

onMounted(() => {
  nextTick(() => {
    if (localModel.value == "unselected") modals.open = true;
  });
});
</script>
